import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { currencySymbol } from '../constants/currency';
import api, { apiErrorMessage, type StudentProfile, type Course } from '../services/api';
import { validateStudentForm, hasErrors, type StudentFormErrors } from '../services/validation';

const STATUS_ICONS: Record<string, { icon: string; label: string; color: string }> = {
  present: { icon: '✅', label: 'Был', color: 'text-green-600 bg-green-50 border-green-200' },
  late: { icon: '⏰', label: 'Опоздал', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  absent: { icon: '❌', label: 'Не был', color: 'text-red-600 bg-red-50 border-red-200' },
  // «Болел» — уважительная причина: занятие из абонемента не списывается.
  excused: { icon: '🤒', label: 'Болел', color: 'text-blue-600 bg-blue-50 border-blue-200' },
  trial: { icon: '⭐', label: 'Пробное', color: 'text-purple-600 bg-purple-50 border-purple-200' },
};

const RELATION_OPTIONS = ['Мама', 'Папа', 'Бабушка', 'Дедушка', 'Тетя', 'Дядя', 'Опекун'];

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  monthly: 'Месяц',
  multi_month: 'Несколько месяцев',
  single_lesson: 'Разовое занятие',
  partial: 'Частичная оплата',
  full: 'Произвольная сумма',
};

export default function StudentCardPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { permissions, user } = useAuth();
  const curSymbol = currencySymbol(user?.currency);
  const [profile, setProfile] = useState<StudentProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);

  // ── Edit mode state ───────────────────────────────────────────────
  // ── Edit mode state ───────────────────────────────────────────────
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    age: '',
    birth_date: '',
    phone: '',
    telegram: '',
    parent_contact: '',
    parent_name: '',
    parent_relation: '',
    photo_url: '',
  });
  const [editFormErrors, setEditFormErrors] = useState<StudentFormErrors>({});
  const [allCourses, setAllCourses] = useState<Course[]>([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);

  // ── Archive modal state ────────────────────────────────────────────
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // ── Payment modal state ────────────────────────────────────────────
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })(),
    payment_type: 'monthly' as string,
    payment_method: 'cash' as string,
    course_id: '',
    comment: '',
    // Сколько занятий входит в абонемент ученика (по умолчанию 8).
    lessons_per_month: '8',
  });
  const [paymentErrors, setPaymentErrors] = useState<Record<string, string>>({});
  const [allCoursesList, setAllCoursesList] = useState<Course[]>([]);
  // Ручная правка абонемента (+/− занятия)
  const [adjustingLessons, setAdjustingLessons] = useState(false);

  // ── Payment receipt modal state ───────────────────────────────────
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptPayment, setReceiptPayment] = useState<any>(null);

  // ── Photo modal ───────────────────────────────────────────────────
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  // ── Photo upload ref ──────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    loadProfile(id);
  }, [id]);

  // ── Абонемент: +/− занятия вручную ─────────────────────────────────
  const handleAdjustLessons = async (delta: number) => {
    if (!id || adjustingLessons) return;
    setAdjustingLessons(true);
    try {
      await api.adjustStudentLessons(id, delta);
      await loadProfile(id);
    } catch (e) {
      alert(apiErrorMessage(e, 'Не удалось изменить абонемент'));
    } finally {
      setAdjustingLessons(false);
    }
  };

  const loadProfile = async (studentId: string) => {
    try {
      setLoading(true);
      const data = await api.getStudentProfile(studentId);
      setProfile(data);
      setError(null);
    } catch (e) {
      console.error('Failed to load student profile:', e);
      setError('Не удалось загрузить данные ученика');
    } finally {
      setLoading(false);
    }
  };

  // ── Payment modal helpers ───────────────────────────────────────────

  const openPaymentModal = async () => {
    if (!profile) return;

    // Load ALL courses dynamically, not just student's enrolled ones
    let availableCourses: Course[];
    try {
      const cData = await api.getCourses();
      availableCourses = cData.courses;
      setAllCoursesList(availableCourses);
    } catch (e) {
      console.error('Failed to load courses:', e);
      availableCourses = profile.courses;
      setAllCoursesList(availableCourses);
    }

    const firstCourse = availableCourses[0] || profile.courses[0];
    const monthlyPrice = firstCourse ? (parseFloat(firstCourse.monthly_price || firstCourse.price) || 0) : 0;

    setPaymentForm({
      amount: String(monthlyPrice),
      payment_date: new Date().toISOString().split('T')[0],
      payment_type: 'monthly',
      payment_method: 'cash',
      course_id: firstCourse?.id || (profile.courses[0]?.id || ''),
      comment: '',
      lessons_per_month: '8',
    });
    setPaymentErrors({});
    setShowPaymentModal(true);
  };

  const handlePaymentTypeChange = (type: string) => {
    if (!profile) return;

    const allAvailable = allCoursesList.length > 0 ? allCoursesList : profile.courses;
    const selectedCourse = allAvailable.find(c => c.id === paymentForm.course_id)
      || profile.courses.find(c => c.id === paymentForm.course_id);
    const monthlyPrice = parseFloat(selectedCourse?.monthly_price || selectedCourse?.price || '0') || 0;
    const lessonPrice = parseFloat(selectedCourse?.lesson_price || '0') || 0;

    let amount = 0;

    switch (type) {
      case 'monthly':
        amount = monthlyPrice;
        break;
      case 'multi_month':
        amount = monthlyPrice * 3;
        break;
      case 'single_lesson':
        amount = lessonPrice || Math.round(monthlyPrice / 4);
        break;
      case 'partial':
        amount = Math.round(monthlyPrice / 2);
        break;
      case 'full':
        amount = 0;
        break;
    }

    setPaymentForm(f => ({
      ...f,
      payment_type: type,
      amount: amount ? String(amount) : '',
    }));
  };

  const validatePaymentForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!paymentForm.course_id) errors.course_id = 'Пожалуйста, выберите курс';
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      errors.amount = 'Сумма должна быть больше нуля';
    }
    if (!paymentForm.payment_date) errors.payment_date = 'Укажите дату платежа';
    setPaymentErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSavePayment = async () => {
    if (!id || savingPayment) return;
    if (!validatePaymentForm()) return;
    setSavingPayment(true);
    try {
      const lessonsPerMonth = parseInt(paymentForm.lessons_per_month, 10);
      await api.createPayment({
        student_id: id,
        course_id: paymentForm.course_id,
        amount: parseFloat(paymentForm.amount) || 0,
        payment_date: paymentForm.payment_date,
        payment_type: paymentForm.payment_type,
        payment_method: paymentForm.payment_method,
        comment: paymentForm.comment,
        lessons_per_month: Number.isFinite(lessonsPerMonth) && lessonsPerMonth > 0 ? lessonsPerMonth : undefined,
      });
      setShowPaymentModal(false);
      setPaymentErrors({});
      await loadProfile(id);
    } catch (e) {
      console.error('Failed to save payment:', e);
      alert(apiErrorMessage(e, '❌ Не удалось сохранить платеж. Попробуйте позже или обратитесь в поддержку.'));
    } finally {
      setSavingPayment(false);
    }
  };

  // ── Edit helpers ────────────────────────────────────────────────────

  const startEditing = async () => {
    if (!profile) return;
    const { student } = profile;
    setEditForm({
      first_name: student.first_name || '',
      last_name: student.last_name || '',
      age: student.age || '',
      birth_date: student.birth_date || '',
      phone: student.phone || '',
      telegram: student.telegram || '',
      parent_contact: student.parent_contact || '',
      parent_name: student.parent_name || '',
      parent_relation: student.parent_relation || '',
      photo_url: student.photo_url || '',
    });
    setSelectedCourseIds(student.course_ids ? student.course_ids.split(',').filter(Boolean) : []);
    try {
      const cData = await api.getCourses();
      setAllCourses(cData.courses);
    } catch (e) { console.error(e); }
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setEditFormErrors({});
    setEditForm({
      first_name: '', last_name: '', age: '', birth_date: '',
      phone: '', telegram: '', parent_contact: '', parent_name: '',
      parent_relation: '', photo_url: '',
    });
  };

  const handleEditFormChange = (field: string, value: string) => {
    setEditForm(f => ({ ...f, [field]: value }));
    // Clear the error for this field as user types
    if (field in editFormErrors) {
      setEditFormErrors(prev => {
        const next = { ...prev };
        delete next[field as keyof StudentFormErrors];
        return next;
      });
    }
  };

  const saveEdits = async () => {
    if (!profile || !id) return;

    // Validate form
    const errors = validateStudentForm(editForm);
    setEditFormErrors(errors);
    if (hasErrors(errors)) return;

    setSaving(true);
    try {
      await api.updateStudent(id, {
        first_name: editForm.first_name,
        last_name: editForm.last_name,
        age: editForm.age || undefined,
        birth_date: editForm.birth_date || undefined,
        phone: editForm.phone || undefined,
        telegram: editForm.telegram || undefined,
        parent_contact: editForm.parent_contact || undefined,
        parent_name: editForm.parent_name || undefined,
        parent_relation: editForm.parent_relation || undefined,
        photo_url: editForm.photo_url || undefined,
        course_ids: selectedCourseIds.join(','),
      });
      setEditFormErrors({});
      await loadProfile(id);
      setEditing(false);
    } catch (e) {
      console.error('Failed to save:', e);
      alert(apiErrorMessage(e, 'Ошибка при сохранении'));
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const result = ev.target?.result as string;
      setEditForm(f => ({ ...f, photo_url: result }));
    };
    reader.readAsDataURL(file);
  };

  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/school/students');
    }
  };

  const formatParentDisplay = () => {
    if (!profile) return null;
    const { student } = profile;
    const relation = student.parent_relation;
    const name = student.parent_name;
    const contact = student.parent_contact;

    if (relation && name) {
      return { relation, name, contact, full: `${relation}: ${name}` };
    }
    if (name) {
      return { relation: '', name, contact, full: name };
    }
    if (contact) {
      return { relation: '', name: '', contact, full: contact };
    }
    return null;
  };

  // ── Loading state ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="p-4 space-y-4 animate-fade-in">
        <div className="skeleton h-6 w-24 mb-2" />
        <div className="skeleton h-36 w-full rounded-3xl" />
        <div className="skeleton h-24 w-full rounded-2xl" />
        <div className="skeleton h-40 w-full rounded-2xl" />
        <div className="skeleton h-32 w-full rounded-2xl" />
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────
  if (error || !profile) {
    return (
      <div className="p-4 animate-fade-in">
        <button onClick={handleBack} className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1 mb-4">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Назад
        </button>
        <div className="tg-card flex flex-col items-center py-8 text-center">
          <span className="text-4xl mb-3">😔</span>
          <p className="text-sm text-[var(--tg-theme-hint-color)] mb-4">{error || 'Ученик не найден'}</p>
          <button onClick={() => id && loadProfile(id)} className="tg-button text-sm">Попробовать снова</button>
        </div>
      </div>
    );
  }

  const { student, courses, payments, total_paid, achievements, attendance } = profile;
  // Абонемент: null — пакета нет (оплата без количества занятий)
  const subscription = profile.subscription || null;
  const courseNames = courses.map(c => c.title).join(', ') || '—';
  const initials = (student.first_name?.[0] || '') + (student.last_name?.[0] || '');
  const parentInfo = formatParentDisplay();
  const displayedHistory = showAllHistory ? attendance.history : attendance.history.slice(0, 5);

  // ── RENDER: Edit mode ─────────────────────────────────────────────
  if (editing) {
    return (
      <div className="p-4 space-y-4 animate-fade-in pb-24">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button onClick={cancelEditing} className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
            Отмена
          </button>
          <h2 className="text-base font-semibold text-[var(--tg-theme-text-color)]">Редактирование</h2>
          <button onClick={saveEdits} disabled={saving}
            className="tg-button text-sm py-1.5 px-4 disabled:opacity-50">
            {saving ? '💾' : '✓'} Сохранить
          </button>
        </div>

        {/* Photo preview + upload */}
        <div className="tg-card flex flex-col items-center gap-3">
          <div className="relative">
            {editForm.photo_url ? (
              <img src={editForm.photo_url} alt="" className="w-24 h-24 rounded-2xl object-cover border-2 border-[var(--tg-theme-button-color)]/30 shadow-md" />
            ) : (
              <div className="w-24 h-24 rounded-2xl bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-3xl font-bold text-[var(--tg-theme-hint-color)] border-2 border-dashed border-[var(--tg-theme-hint-color)]/30">
                {editForm.first_name?.[0]}{editForm.last_name?.[0]}
              </div>
            )}
            <button onClick={() => fileInputRef.current?.click()}
              className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[var(--tg-theme-button-color)] text-white flex items-center justify-center shadow-md hover:scale-110 transition-transform">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" /></svg>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
          </div>
          <button onClick={() => fileInputRef.current?.click()} className="text-xs text-[var(--tg-theme-button-color)]">
            Загрузить фото
          </button>
          {editForm.photo_url && (
            <button onClick={() => setEditForm(f => ({ ...f, photo_url: '' }))}
              className="text-xs text-red-500">
              Удалить фото
            </button>
          )}
        </div>

        {/* Name fields */}
        <div className="tg-card space-y-3">
          <h3 className="text-xs font-semibold text-[var(--tg-theme-hint-color)] uppercase tracking-wide">Личные данные</h3>
          <div className="flex gap-2">
            <div className="flex-1">
              <input value={editForm.first_name} onChange={e => handleEditFormChange('first_name', e.target.value)}
                placeholder="Имя *"
                className={`w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30 ${editFormErrors.first_name ? 'ring-2 ring-red-300' : ''}`} />
              {editFormErrors.first_name && <p className="text-xs text-red-500 mt-1">{editFormErrors.first_name}</p>}
            </div>
            <div className="flex-1">
              <input value={editForm.last_name} onChange={e => handleEditFormChange('last_name', e.target.value)}
                placeholder="Фамилия *"
                className={`w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30 ${editFormErrors.last_name ? 'ring-2 ring-red-300' : ''}`} />
              {editFormErrors.last_name && <p className="text-xs text-red-500 mt-1">{editFormErrors.last_name}</p>}
            </div>
          </div>
          <div className="flex gap-2">
            <div className="w-1/3">
              <input value={editForm.age} onChange={e => handleEditFormChange('age', e.target.value)}
                placeholder="Возраст" type="number" min="3" max="99"
                className={`w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30 ${editFormErrors.age ? 'ring-2 ring-red-300' : ''}`} />
              {editFormErrors.age && <p className="text-xs text-red-500 mt-1">{editFormErrors.age}</p>}
            </div>
            <input value={editForm.birth_date} onChange={e => handleEditFormChange('birth_date', e.target.value)}
              placeholder="Дата рождения" type="date" className="flex-1 px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30" />
          </div>
        </div>

        {/* Contact fields */}
        <div className="tg-card space-y-3">
          <h3 className="text-xs font-semibold text-[var(--tg-theme-hint-color)] uppercase tracking-wide">Контакты</h3>
          <div>
            <input value={editForm.phone} onChange={e => handleEditFormChange('phone', e.target.value)}
              placeholder="Телефон ученика"
              className={`w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30 ${editFormErrors.phone ? 'ring-2 ring-red-300' : ''}`} />
            {editFormErrors.phone && <p className="text-xs text-red-500 mt-1">{editFormErrors.phone}</p>}
          </div>
          <input value={editForm.telegram} onChange={e => handleEditFormChange('telegram', e.target.value)}
            placeholder="@telegram" className="w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30" />
        </div>

        {/* Parent fields */}
        <div className="tg-card space-y-3">
          <h3 className="text-xs font-semibold text-[var(--tg-theme-hint-color)] uppercase tracking-wide">👤 Родитель / контактное лицо</h3>
          <div className="flex gap-2">
            <select value={editForm.parent_relation} onChange={e => setEditForm(f => ({ ...f, parent_relation: e.target.value }))}
              className="w-2/5 px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30">
              <option value="">Кто</option>
              {RELATION_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input value={editForm.parent_name} onChange={e => setEditForm(f => ({ ...f, parent_name: e.target.value }))}
              placeholder="Имя" className="flex-1 px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30" />
          </div>
          <div>
            <input value={editForm.parent_contact} onChange={e => handleEditFormChange('parent_contact', e.target.value)}
              placeholder="Телефон родителя"
              className={`w-full px-3 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30 ${editFormErrors.parent_contact ? 'ring-2 ring-red-300' : ''}`} />
            {editFormErrors.parent_contact && <p className="text-xs text-red-500 mt-1">{editFormErrors.parent_contact}</p>}
          </div>
        </div>

        {/* Courses */}
        <div className="tg-card space-y-3">
          <h3 className="text-xs font-semibold text-[var(--tg-theme-hint-color)] uppercase tracking-wide">📚 Курсы</h3>
          <div className="flex flex-wrap gap-2">
            {allCourses.map(c => {
              const selected = selectedCourseIds.includes(c.id);
              return (
                <button key={c.id} onClick={() => {
                  setSelectedCourseIds(prev =>
                    selected ? prev.filter(x => x !== c.id) : [...prev, c.id]
                  );
                }}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    selected
                      ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white'
                      : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                  }`}>
                  {c.title}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── RENDER: View mode ─────────────────────────────────────────────
  return (
    <div className="p-4 space-y-4 animate-fade-in pb-24">
      {/* Photo modal overlay */}
      {showPhotoModal && student.photo_url && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowPhotoModal(false)}>
          <div className="relative max-w-sm w-full">
            <img src={student.photo_url} alt=""
              className="w-full rounded-3xl object-cover shadow-2xl" />
            <button onClick={() => setShowPhotoModal(false)}
              className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm text-white flex items-center justify-center text-lg hover:bg-white/30 transition-colors">
              ✕
            </button>
            <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/70 to-transparent rounded-b-3xl">
              <p className="text-white font-bold text-lg">{student.first_name} {student.last_name}</p>
              {parentInfo && (
                <p className="text-white/70 text-sm mt-0.5">{parentInfo.full}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Payment modal ────────────────────────────────────────────── */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowPaymentModal(false)}>
          <div className="bg-[var(--tg-theme-bg-color)] rounded-3xl w-full max-w-md max-h-[85vh] overflow-y-auto p-5 pb-8 shadow-2xl animate-slide-up"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[var(--tg-theme-text-color)]">➕ Добавить платеж</h3>
              <button onClick={() => setShowPaymentModal(false)}
                className="w-8 h-8 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-sm hover:opacity-70 transition-opacity">
                ✕
              </button>
            </div>

            {/* Course selector — all active courses */}
            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Курс *</label>
              {(allCoursesList.length > 0 ? allCoursesList : courses).length > 0 ? (
                <div className="flex gap-1.5 flex-wrap">
                  {(allCoursesList.length > 0 ? allCoursesList : courses).map(c => (
                    <button key={c.id}
                      onClick={() => {
                        const mp = parseFloat(c.monthly_price || c.price) || 0;
                        setPaymentForm(f => ({
                          ...f,
                          course_id: c.id,
                          amount: f.payment_type === 'monthly' ? String(mp) : f.amount,
                        }));
                        setPaymentErrors(prev => { const n = {...prev}; delete n.course_id; return n; });
                      }}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        paymentForm.course_id === c.id
                          ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)] text-white'
                          : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                      } ${paymentErrors.course_id ? 'ring-2 ring-red-300' : ''}`}>
                      {c.title}
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[var(--tg-theme-hint-color)]">Нет доступных курсов</p>
              )}
              {paymentErrors.course_id && (
                <p className="text-xs text-red-500 mt-1">{paymentErrors.course_id}</p>
              )}
            </div>

            {/* Payment type */}
            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Тип платежа</label>
              <div className="grid grid-cols-2 gap-1.5">
                {[
                  { value: 'monthly', label: '📆 Месяц' },
                  { value: 'multi_month', label: '📚 Несколько месяцев' },
                  { value: 'single_lesson', label: '📖 Разовое занятие' },
                  { value: 'partial', label: '✂️ Частичная' },
                  { value: 'full', label: '📝 Произвольная' },
                ].map(opt => (
                  <button key={opt.value}
                    onClick={() => handlePaymentTypeChange(opt.value)}
                    className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                      paymentForm.payment_type === opt.value
                        ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]/10 text-[var(--tg-theme-button-color)]'
                        : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                    }`}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment method */}
            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Способ оплаты</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setPaymentForm(f => ({ ...f, payment_method: 'cash' }))}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                    paymentForm.payment_method === 'cash'
                      ? 'border-green-400 bg-green-50 text-green-700'
                      : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                  }`}>
                  💵 Наличные
                </button>
                <button
                  onClick={() => setPaymentForm(f => ({ ...f, payment_method: 'card' }))}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                    paymentForm.payment_method === 'card'
                      ? 'border-blue-400 bg-blue-50 text-blue-700'
                      : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                  }`}>
                  💳 Карта
                </button>
              </div>
            </div>

            {/* Amount */}
            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Сумма ({curSymbol}) *</label>
              <input type="number" value={paymentForm.amount}
                onChange={e => {
                  setPaymentForm(f => ({ ...f, amount: e.target.value }));
                  setPaymentErrors(prev => { const n = {...prev}; delete n.amount; return n; });
                }}
                className={`w-full px-4 py-3 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-lg font-bold outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30 ${paymentErrors.amount ? 'ring-2 ring-red-300' : ''}`}
                placeholder="0" />
              {paymentErrors.amount && (
                <p className="text-xs text-red-500 mt-1">{paymentErrors.amount}</p>
              )}
            </div>

            {/* Lessons per month (абонемент) */}
            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">
                Сколько занятий входит в месячный абонемент?
              </label>
              <div className="flex gap-2 mb-2">
                {[8, 12, 16].map(n => (
                  <button key={n} type="button"
                    onClick={() => setPaymentForm(f => ({ ...f, lessons_per_month: String(n) }))}
                    className={`flex-1 px-3 py-2 rounded-xl text-sm font-semibold border transition-all ${
                      paymentForm.lessons_per_month === String(n)
                        ? 'border-[var(--tg-theme-button-color)] bg-[var(--tg-theme-button-color)]/10 text-[var(--tg-theme-button-color)]'
                        : 'border-[var(--tg-theme-section-separator-color)] bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-text-color)]'
                    }`}>
                    {n}
                  </button>
                ))}
              </div>
              <input type="number" min={1} step={1} value={paymentForm.lessons_per_month}
                onChange={e => setPaymentForm(f => ({ ...f, lessons_per_month: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30"
                placeholder="Например: 8" />
              <p className="text-[11px] text-[var(--tg-theme-hint-color)] mt-1">
                Отсчёт посещений начнётся с первого занятия. Оставьте поле пустым, если абонемент не нужен.
              </p>
            </div>

            {/* Date */}
            <div className="mb-4">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Дата платежа *</label>
              <input type="date" value={paymentForm.payment_date}
                onChange={e => {
                  setPaymentForm(f => ({ ...f, payment_date: e.target.value }));
                  setPaymentErrors(prev => { const n = {...prev}; delete n.payment_date; return n; });
                }}
                className={`w-full px-4 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30 ${paymentErrors.payment_date ? 'ring-2 ring-red-300' : ''}`} />
              {paymentErrors.payment_date && (
                <p className="text-xs text-red-500 mt-1">{paymentErrors.payment_date}</p>
              )}
            </div>

            {/* Comment */}
            <div className="mb-5">
              <label className="text-xs font-medium text-[var(--tg-theme-hint-color)] mb-1.5 block">Комментарий</label>
              <input type="text" value={paymentForm.comment}
                onChange={e => setPaymentForm(f => ({ ...f, comment: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl bg-[var(--tg-theme-secondary-bg-color)] text-sm outline-none focus:ring-2 focus:ring-[var(--tg-theme-button-color)]/30"
                placeholder="Например: оплата за июль" />
            </div>

            {/* Save button */}
            <button onClick={handleSavePayment} disabled={savingPayment}
              className="w-full tg-button text-base disabled:opacity-50">
              {savingPayment ? '⏳ Сохранение...' : '✓ Сохранить платеж'}
            </button>
          </div>
        </div>
      )}

      {/* ── Payment Receipt Modal ───────────────────────────────────── */}
      {showReceiptModal && receiptPayment && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowReceiptModal(false)}>
          <div className="bg-[var(--tg-theme-bg-color)] rounded-3xl w-full max-w-sm p-6 shadow-2xl animate-slide-up"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold flex items-center gap-2">🧾 Чек</h3>
              <button onClick={() => setShowReceiptModal(false)}
                className="w-8 h-8 rounded-full bg-[var(--tg-theme-secondary-bg-color)] flex items-center justify-center text-sm hover:opacity-70">✕</button>
            </div>

            <div className="space-y-4">
              {/* Amount */}
              <div className="text-center">
                <span className="text-4xl font-bold text-green-600">
                  +{parseFloat(receiptPayment.amount).toLocaleString()} {curSymbol}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-3 bg-[var(--tg-theme-secondary-bg-color)] rounded-2xl p-4">
                <div className="flex justify-between">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">Дата</span>
                  <span className="text-xs font-medium">{receiptPayment.payment_date || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">Тип</span>
                  <span className="text-xs font-medium">
                    {PAYMENT_TYPE_LABELS[receiptPayment.payment_type] || receiptPayment.payment_type}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">Курс</span>
                  <span className="text-xs font-medium">
                    {courses.find((c: any) => c.id === receiptPayment.course_id)?.title || '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">Способ</span>
                  <span className="text-xs font-medium">
                    {receiptPayment.payment_method === 'card' ? '💳 Карта' : '💵 Наличные'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-[var(--tg-theme-hint-color)]">Статус</span>
                  <span className="text-xs font-medium text-green-600">✅ Оплачено</span>
                </div>
                {receiptPayment.comment && (
                  <div className="flex justify-between">
                    <span className="text-xs text-[var(--tg-theme-hint-color)]">Комментарий</span>
                    <span className="text-xs font-medium text-right max-w-[200px]">{receiptPayment.comment}</span>
                  </div>
                )}
              </div>

              <button onClick={() => setShowReceiptModal(false)}
                className="w-full py-3 rounded-2xl border border-[var(--tg-theme-section-separator-color)] text-sm font-medium hover:opacity-80 transition-all">
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Back + Edit buttons ──────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <button onClick={handleBack} className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1 hover:opacity-80 transition-opacity">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
          Назад
        </button>
        {permissions.canEditStudents && (
          <button onClick={startEditing} className="text-sm text-[var(--tg-theme-button-color)] flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[var(--tg-theme-button-color)]/10 hover:bg-[var(--tg-theme-button-color)]/20 transition-all">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
            Редактировать
          </button>
        )}
      </div>

      {/* ── Hero profile card ────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--tg-theme-button-color)] to-[var(--tg-theme-button-color)]/70 text-white p-6 shadow-lg">
        <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10" />
        <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-white/5" />

        <div className="relative flex flex-col items-center">
          <button onClick={() => student.photo_url && setShowPhotoModal(true)}
            className={`transition-transform ${student.photo_url ? 'hover:scale-105 active:scale-95 cursor-pointer' : 'cursor-default'}`}>
            {student.photo_url ? (
              <img src={student.photo_url} alt="" className="w-44 h-44 md:w-48 md:h-48 rounded-3xl border-2 border-white/30 object-cover shadow-md" />
            ) : (
              <div className="w-44 h-44 md:w-48 md:h-48 rounded-3xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-5xl font-bold border border-white/20 shadow-md">
                {initials}
              </div>
            )}
          </button>
          <div className="text-center mt-4">
            <h2 className="text-2xl font-bold">
              {student.first_name} {student.last_name}
            </h2>
            <div className="flex flex-wrap justify-center gap-2 mt-2">
              {student.age && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm">
                  {student.age} лет
                </span>
              )}
              <span className="text-xs px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm">
                {attendance.total_lessons} занятий
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full backdrop-blur-sm ${
                attendance.attendance_rate >= 80 ? 'bg-green-400/30' :
                attendance.attendance_rate >= 50 ? 'bg-amber-400/30' : 'bg-red-400/30'
              }`}>
                {attendance.attendance_rate}% посещ.
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Quick info grid ──────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-2">
        <div className="tg-card !p-3 flex items-center gap-3">
          <span className="text-xl">📚</span>
          <div className="min-w-0">
            <p className="text-[10px] text-[var(--tg-theme-hint-color)]">Курсы</p>
            <p className="text-xs font-medium truncate">{courseNames}</p>
          </div>
        </div>
        <div className="tg-card !p-3 flex items-center gap-3">
          <span className="text-xl">{parentInfo?.relation === 'Папа' || parentInfo?.relation === 'Дедушка' || parentInfo?.relation === 'Дядя' ? '👨' : parentInfo?.relation === 'Бабушка' || parentInfo?.relation === 'Тетя' || parentInfo?.relation === 'Мама' ? '👩' : '👤'}</span>
          <div className="min-w-0">
            <p className="text-[10px] text-[var(--tg-theme-hint-color)]">{parentInfo?.relation || 'Родитель'}</p>
            <p className="text-xs font-medium truncate">{parentInfo ? (parentInfo.name || parentInfo.contact || '—') : '—'}</p>
          </div>
        </div>
        <div className="tg-card !p-3 flex items-center gap-3">
          <span className="text-xl">🎂</span>
          <div className="min-w-0">
            <p className="text-[10px] text-[var(--tg-theme-hint-color)]">День рождения</p>
            <p className="text-xs font-medium">{student.birth_date || 'Не указано'}</p>
          </div>
        </div>
        <div className="tg-card !p-3 flex items-center gap-3">
          <span className="text-xl">{attendance.last_visit ? '📅' : '—'}</span>
          <div className="min-w-0">
            <p className="text-[10px] text-[var(--tg-theme-hint-color)]">Последний визит</p>
            <p className="text-xs font-medium">{attendance.last_visit || 'Нет данных'}</p>
          </div>
        </div>
      </div>

      {/* ── Абонемент (пакет занятий) ─────────────────────────────── */}
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <span>🎫</span> Абонемент
        </h3>

        {subscription ? (
          <>
            <div className="flex items-end justify-between mb-2">
              <div>
                <p className="text-2xl font-bold text-[var(--tg-theme-text-color)]">
                  {subscription.lessons_left}
                  <span className="text-sm font-medium text-[var(--tg-theme-hint-color)]"> из {subscription.lessons_total}</span>
                </p>
                <p className="text-[11px] text-[var(--tg-theme-hint-color)]">
                  {subscription.lessons_left > 0
                    ? 'занятий осталось'
                    : 'абонемент исчерпан — пора продлить'}
                </p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border shrink-0 ${
                subscription.lessons_left > 0
                  ? 'bg-green-50 text-green-600 border-green-200'
                  : 'bg-red-50 text-red-600 border-red-200'
              }`}>
                {subscription.status === 'active' ? 'активен' : subscription.status === 'exhausted' ? 'исчерпан' : subscription.status}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[var(--tg-theme-button-color)] transition-all"
                style={{
                  width: `${subscription.lessons_total > 0
                    ? (subscription.lessons_used / subscription.lessons_total) * 100
                    : 0}%`,
                }}
              />
            </div>
            <p className="text-[11px] text-[var(--tg-theme-hint-color)] mt-1.5">
              Списано {subscription.lessons_used}. Занятие списывают «Был» и «Опоздал»;
              «Болел», «Пробное» и «Не был» — нет.
            </p>
          </>
        ) : (
          <p className="text-xs text-[var(--tg-theme-hint-color)] leading-relaxed">
            Абонемента нет. Он заводится при оплате, если указать количество занятий
            в месяце, — или добавьте занятия вручную кнопкой ниже.
          </p>
        )}

        {permissions.canAddPayments && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--tg-theme-section-separator-color)]">
            <span className="text-[11px] text-[var(--tg-theme-hint-color)] flex-1">
              Изменить занятия вручную
            </span>
            <button
              onClick={() => handleAdjustLessons(-1)}
              disabled={adjustingLessons || !subscription || subscription.lessons_left <= 0}
              className="w-9 h-8 rounded-xl border border-[var(--tg-theme-section-separator-color)] text-sm font-bold text-[var(--tg-theme-text-color)] transition-all active:scale-95 disabled:opacity-40"
            >
              −1
            </button>
            <button
              onClick={() => handleAdjustLessons(1)}
              disabled={adjustingLessons}
              className="w-9 h-8 rounded-xl border border-[var(--tg-theme-button-color)] text-sm font-bold text-[var(--tg-theme-button-color)] transition-all active:scale-95 disabled:opacity-40"
            >
              +1
            </button>
            <button
              onClick={() => handleAdjustLessons(8)}
              disabled={adjustingLessons}
              className="px-3 h-8 rounded-xl bg-[var(--tg-theme-button-color)] text-[var(--tg-theme-button-text-color)] text-xs font-semibold transition-all active:scale-95 disabled:opacity-40"
            >
              {adjustingLessons ? '…' : '+8'}
            </button>
          </div>
        )}
      </div>

      {/* ── Contact info ─────────────────────────────────────────────── */}
      {(student.phone || student.telegram || parentInfo) && (
        <div className="tg-card">
          <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
            <span>📱</span> Контакты
          </h3>
          <div className="space-y-2 text-sm">
            {student.phone && (
              <div className="flex items-center gap-2">
                <span className="text-[var(--tg-theme-hint-color)]">📞</span>
                <span>{student.phone}</span>
              </div>
            )}
            {student.telegram && (
              <div className="flex items-center gap-2">
                <span className="text-[var(--tg-theme-hint-color)]">✈️</span>
                <span>{student.telegram}</span>
              </div>
            )}
            {parentInfo && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-[var(--tg-theme-hint-color)]">👨‍👩‍👧</span>
                  <span>
                    {parentInfo.relation && <span className="font-medium">{parentInfo.relation}: </span>}
                    {parentInfo.name && <span>{parentInfo.name}</span>}
                  </span>
                </div>
                {parentInfo.contact && (
                  <div className="flex items-center gap-2 ml-6">
                    <span className="text-[var(--tg-theme-hint-color)]">📞</span>
                    <span>{parentInfo.contact}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Payment card ─────────────────────────────────────────────── */}
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <span>💰</span> Оплата
        </h3>

        {/* Total paid summary */}
        <div className="flex items-center justify-between mb-4 px-1">
          <span className="text-sm text-[var(--tg-theme-hint-color)]">Всего оплачено</span>
          <span className="text-xl font-bold text-green-600">
            {total_paid.toLocaleString()} {curSymbol}
          </span>
        </div>

        {/* Add payment button — or hint when no courses */}
        {permissions.canAddPayments && courses.length > 0 && (
          <button
            onClick={openPaymentModal}
            className="w-full py-3 rounded-2xl font-semibold text-sm transition-all active:scale-[0.98] tg-button"
          >
            ➕ Добавить платеж
          </button>
        )}
        {permissions.canAddPayments && courses.length === 0 && (
          <div className="text-center py-3 px-4 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-xs text-amber-700">
              📝 Запишите ученика на курс через «Редактировать», чтобы добавить платеж
            </p>
          </div>
        )}

        {/* Payment journal toggle */}
        {payments.length > 0 && (
          <>
            <button
              onClick={() => setShowAllPayments(!showAllPayments)}
              className="w-full mt-3 py-2 text-xs font-medium text-[var(--tg-theme-button-color)] hover:opacity-80 transition-opacity"
            >
              {showAllPayments ? '▲ Скрыть историю' : `▼ История платежей (${payments.length})`}
            </button>

            {showAllPayments && (
              <div className="mt-2 space-y-1 pt-3 border-t border-[var(--tg-theme-section-separator-color)]">
                {payments.map(p => {
                  const typeLabel = PAYMENT_TYPE_LABELS[p.payment_type] || p.payment_type;
                  const courseName = courses.find(c => c.id === p.course_id)?.title || '';
                  return (
                    <div key={p.id} onClick={() => { setReceiptPayment(p); setShowReceiptModal(true); }}
                      className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-[var(--tg-theme-secondary-bg-color)] transition-colors cursor-pointer active:scale-[0.99]">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xs font-mono text-[var(--tg-theme-hint-color)] shrink-0 w-20">
                          {p.payment_date || '—'}
                        </span>
                        <span className="text-sm font-semibold text-green-600 shrink-0">
                          +{parseFloat(p.amount).toLocaleString()} {curSymbol}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--tg-theme-secondary-bg-color)] text-[var(--tg-theme-hint-color)] shrink-0">
                          {typeLabel}
                        </span>
                        <span className="text-[10px] text-[var(--tg-theme-hint-color)] shrink-0">
                          {p.payment_method === 'card' ? '💳' : '💵'}
                        </span>
                        {courseName && (
                          <span className="text-[10px] text-[var(--tg-theme-hint-color)] truncate">
                            {courseName}
                          </span>
                        )}
                      </div>
                      {p.comment && (
                        <span className="text-[10px] text-[var(--tg-theme-hint-color)] truncate max-w-[80px] ml-2">
                          💬 {p.comment}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {payments.length === 0 && (
          <div className="text-center pt-3">
            <p className="text-xs text-[var(--tg-theme-hint-color)]">
              Платежей пока нет. Нажмите «Добавить платеж», чтобы создать первый.
            </p>
          </div>
        )}
      </div>

      {/* ── Attendance stats card ────────────────────────────────────── */}
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <span>📊</span> Посещаемость
        </h3>

        <div className="flex items-center gap-6 mb-4">
          <div className="relative w-20 h-20">
            <svg className="w-20 h-20 -rotate-90" viewBox="0 0 72 72">
              <circle cx="36" cy="36" r="30" fill="none" stroke="#f0f0f0" strokeWidth="6" />
              <circle
                cx="36" cy="36" r="30" fill="none"
                stroke={attendance.attendance_rate >= 80 ? '#22c55e' : attendance.attendance_rate >= 50 ? '#f59e0b' : '#ef4444'}
                strokeWidth="6"
                strokeDasharray={`${(attendance.attendance_rate / 100) * 188.5} 188.5`}
                strokeLinecap="round"
                className="transition-all duration-1000"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className={`text-lg font-bold ${
                attendance.attendance_rate >= 80 ? 'text-green-500' :
                attendance.attendance_rate >= 50 ? 'text-amber-500' : 'text-red-500'
              }`}>
                {attendance.attendance_rate}%
              </span>
            </div>
          </div>
          <div className="flex-1 space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">✅ Присутствовал</span>
              <span className="font-semibold text-green-600">{attendance.present}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">⏰ Опоздал</span>
              <span className="font-semibold text-amber-600">{attendance.late}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">❌ Отсутствовал</span>
              <span className="font-semibold text-red-600">{attendance.absent}</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1">🤒 Болел</span>
              <span className="font-semibold text-blue-600">{attendance.excused}</span>
            </div>
            <div className="flex items-center justify-between text-xs pt-1 border-t border-[var(--tg-theme-section-separator-color)]">
              <span className="text-[var(--tg-theme-hint-color)]">Всего отметок</span>
              <span className="font-semibold">{attendance.total_records}</span>
            </div>
          </div>
        </div>

        <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
          <div className="h-full bg-green-500 transition-all" style={{ width: `${attendance.total_records > 0 ? (attendance.present / attendance.total_records) * 100 : 0}%` }} />
          <div className="h-full bg-amber-500 transition-all" style={{ width: `${attendance.total_records > 0 ? (attendance.late / attendance.total_records) * 100 : 0}%` }} />
          <div className="h-full bg-red-500 transition-all" style={{ width: `${attendance.total_records > 0 ? (attendance.absent / attendance.total_records) * 100 : 0}%` }} />
        </div>
      </div>

      {/* ── Attendance history ───────────────────────────────────────── */}
      {attendance.history.length > 0 && (
        <div className="tg-card">
          <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
            <span>📋</span> История посещений
          </h3>

          <div className="space-y-1">
            {displayedHistory.map((record) => {
              const statusInfo = STATUS_ICONS[record.status] || STATUS_ICONS.absent;
              const course = courses.find(c => c.id === record.course_id);
              return (
                <div key={record.id} onClick={() => record.lesson_id && navigate(`/lesson/${record.lesson_id}`)}
                  className={`flex items-center gap-3 py-2 px-3 rounded-xl transition-colors ${
                    record.lesson_id ? 'hover:bg-[var(--tg-theme-secondary-bg-color)] cursor-pointer active:scale-[0.99]' : ''
                  }`}>
                  <span className="text-xs font-mono text-[var(--tg-theme-hint-color)] w-24 shrink-0">
                    {record.date}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusInfo.color}`}>
                    {statusInfo.icon} {statusInfo.label}
                  </span>
                  <span className="text-xs text-[var(--tg-theme-hint-color)] truncate flex-1">
                    {course?.title || record.course_id}
                  </span>
                  {record.comment && (
                    <span className="text-[10px] text-[var(--tg-theme-hint-color)] truncate max-w-[80px]">
                      💬 {record.comment}
                    </span>
                  )}
                  {record.lesson_id && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[var(--tg-theme-hint-color)] shrink-0">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>

          {attendance.history.length > 5 && (
            <button
              onClick={() => setShowAllHistory(!showAllHistory)}
              className="w-full mt-2 py-2 text-xs font-medium text-[var(--tg-theme-button-color)] hover:opacity-80 transition-opacity"
            >
              {showAllHistory
                ? '▲ Показать меньше'
                : `▼ Показать все (${attendance.history.length})`}
            </button>
          )}
        </div>
      )}

      {/* ── Achievements ─────────────────────────────────────────────── */}
      <div className="tg-card">
        <h3 className="text-sm font-semibold text-[var(--tg-theme-text-color)] mb-3 flex items-center gap-2">
          <span>🏆</span> Достижения
        </h3>

        {achievements.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {achievements.map(a => (
              <div key={a.id} className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-100">
                <span className="text-xl shrink-0">{a.icon}</span>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-amber-800">{a.title}</p>
                  {a.description && (
                    <p className="text-[10px] text-amber-600 mt-0.5 line-clamp-2">{a.description}</p>
                  )}
                  {a.achieved_at && (
                    <p className="text-[9px] text-amber-400 mt-0.5">{a.achieved_at.split('T')[0]}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center py-4 text-center">
            <span className="text-2xl mb-1">🌟</span>
            <p className="text-xs text-[var(--tg-theme-hint-color)]">Пока нет достижений</p>
          </div>
        )}
      </div>

      {/* ── Archive confirmation modal ─────────────────────────────── */}
      {showArchiveModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 animate-fade-in"
          onClick={() => setShowArchiveModal(false)}>
          <div className="bg-[var(--tg-theme-bg-color)] rounded-3xl w-full max-w-sm p-6 shadow-2xl animate-slide-up text-center"
            onClick={e => e.stopPropagation()}>
            <span className="text-5xl block mb-4">📦</span>
            <h3 className="text-lg font-bold text-[var(--tg-theme-text-color)] mb-2">
              Архивировать ученика?
            </h3>
            <p className="text-sm text-[var(--tg-theme-hint-color)] mb-6">
              {student.first_name} {student.last_name} исчезнет из основного списка, но данные сохранятся.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowArchiveModal(false)}
                className="flex-1 py-3 rounded-2xl border border-[var(--tg-theme-section-separator-color)] text-sm font-medium hover:opacity-80 transition-all">
                Отмена
              </button>
              <button onClick={async () => {
                setArchiving(true);
                try {
                  await api.deleteStudent(student.id);
                  navigate('/school/students');
                } catch (e) {
                  console.error(e);
                  setArchiving(false);
                  setShowArchiveModal(false);
                }
              }} disabled={archiving}
                className="flex-1 py-3 rounded-2xl bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 transition-all active:scale-[0.98] disabled:opacity-50">
                {archiving ? '⏳...' : 'Архивировать'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Archive & Delete buttons ───────────────────────────────── */}
      <div className="space-y-2">
        {permissions.canArchiveStudents && (
          <button onClick={() => setShowArchiveModal(true)}
            className="w-full py-3 rounded-2xl border border-amber-200 text-amber-600 text-sm font-medium hover:bg-amber-50 transition-all active:scale-[0.98]"
          >
            📦 Архивировать
          </button>
        )}
        {permissions.canDeleteStudents && (
          <button
            onClick={async () => {
              if (!window.confirm(`Удалить ученика ${student.first_name} ${student.last_name}? Это действие нельзя отменить.`)) return;
              try {
                await api.deleteStudent(student.id);
                navigate('/school/students');
              } catch (e) { console.error(e); }
            }}
            className="w-full py-3 rounded-2xl border border-red-200 text-red-500 text-sm font-medium hover:bg-red-50 transition-all active:scale-[0.98]"
          >
            Удалить ученика
          </button>
        )}
      </div>
    </div>
  );
}
